import { BOUNTY_LURE, isBounty } from "./bounty.ts";
import { distance, type GameData, type Participant } from "./model.ts";
import { chargeOf, type GameSettings } from "./settings.ts";
import { getStormCenter, getStormState } from "./storm.ts";

export type BotIntent = {
  moveX: number;
  moveY: number;
  fireX: number | null;
  fireY: number | null;
  useBoost: boolean;
  useShield: boolean;
};

/**
 * How long a bot sticks to a decision.
 *
 * Every intent used to be recomputed from nothing, so two piles of energy a pixel
 * apart in value had it walking between them and reaching neither. A person picks
 * a thing and goes; this is that, in seconds.
 */
const COMMITMENT_SECONDS = 1.4;

/** A shot this close is arriving whatever you do — better to catch it than run. */
const SHIELD_WINDOW_SECONDS = 0.42;

function clockOf(game: GameData) {
  return Number.isFinite(game.elapsed) ? game.elapsed : 0;
}

/**
 * What this bot committed to and until when. Kept on the participant, which the
 * server owns and never puts on the wire — nobody else needs to know what a bot
 * is thinking.
 */
function commitment(game: GameData, bot: Participant, choose: () => { kind: string; id: string } | null) {
  const now = clockOf(game);
  const held = bot.bot;
  if (held && held.until > now) return held;
  const picked = choose();
  bot.bot = picked ? { ...picked, until: now + COMMITMENT_SECONDS } : undefined;
  return bot.bot;
}

/** Stable per-bot sign, so two bots do not always strafe the same way. */
function strafeSide(bot: Participant) {
  let hash = 0;
  for (let index = 0; index < bot.id.length; index += 1) hash = (hash * 31 + bot.id.charCodeAt(index)) | 0;
  return hash % 2 === 0 ? 1 : -1;
}

function jitterSeed(bot: Participant) {
  let hash = 7;
  for (let index = 0; index < bot.id.length; index += 1) hash = (hash * 17 + bot.id.charCodeAt(index)) | 0;
  return Math.abs(hash % 1000) / 1000;
}

function nearestCombatTarget(game: GameData, bot: Participant) {
  let nearest: Participant | undefined;
  let nearestScore = Infinity;
  for (const other of game.participants) {
    if (!other.alive || other.id === bot.id) continue;
    // Lower is better: real distance, plus a penalty for a full orbit that will
    // shoot back, less a discount for the advantage this bot already holds — and
    // less the pull of the mark, which is worth crossing some ground for.
    const score = distance(bot.x, bot.y, other.x, other.y)
      + other.orbs * 34
      - Math.max(0, bot.orbs - other.orbs) * 18
      - (isBounty(game, other) ? BOUNTY_LURE : 0);
    if (score < nearestScore) {
      nearest = other;
      nearestScore = score;
    }
  }
  return nearest;
}

function incomingThreat(game: GameData, bot: Participant, awareness: number) {
  let nearest: GameData["shots"][number] | undefined;
  let nearestDistance = Infinity;
  for (const shot of game.shots) {
    if (shot.active === false || shot.owner === bot.id || shot.returning) continue;
    const shotDistance = distance(shot.x, shot.y, bot.x, bot.y);
    if (shotDistance >= awareness || shotDistance >= nearestDistance) continue;
    const length = Math.max(1, Math.hypot(shot.vx, shot.vy));
    const towardX = (bot.x - shot.x) / Math.max(1, shotDistance);
    const towardY = (bot.y - shot.y) / Math.max(1, shotDistance);
    const approach = (shot.vx / length) * towardX + (shot.vy / length) * towardY;
    if (approach > 0.55) {
      nearest = shot;
      nearestDistance = shotDistance;
    }
  }
  return nearest;
}

export function decideBotIntent(game: GameData, bot: Participant, settings: GameSettings): BotIntent {
  const skill = settings.botSkill;
  const side = strafeSide(bot);
  const threat = incomingThreat(game, bot, 95 + skill * 145);
  if (threat) {
    const awayX = bot.x - threat.x;
    const awayY = bot.y - threat.y;
    const length = Math.max(1, Math.hypot(awayX, awayY));
    const threatSpeed = Math.max(1, Math.hypot(threat.vx, threat.vy));

    // Catching a shot turns it into an orb; running from one only avoids a loss.
    // So the shield is the better answer whenever the shot is close enough to be
    // arriving anyway — and a five-second cooldown is not spent on a shot still
    // half a screen away.
    const reachesIn = length / threatSpeed;
    const canShield = bot.shieldCooldown <= 0 && bot.orbs >= 1 && bot.shield <= 0;
    if (canShield && reachesIn <= SHIELD_WINDOW_SECONDS) {
      return {
        // Hold the line and let it come. Sidestepping now would only take the
        // shield out of its path.
        moveX: bot.x + (-threat.vy / threatSpeed) * 30 * side,
        moveY: bot.y + (threat.vx / threatSpeed) * 30 * side,
        fireX: null,
        fireY: null,
        useBoost: false,
        useShield: true,
      };
    }

    return {
      moveX: bot.x + (awayX / length) * 190 + (-threat.vy / threatSpeed) * 90 * side,
      moveY: bot.y + (awayY / length) * 190 + (threat.vx / threatSpeed) * 90 * side,
      fireX: null,
      fireY: null,
      useBoost: bot.orbs >= 4 && bot.boostRemaining <= 0,
      useShield: false,
    };
  }

  const target = nearestCombatTarget(game, bot);
  let ownEnergy: GameData["energy"][number] | undefined;
  let anyEnergy: GameData["energy"][number] | undefined;
  let ownEnergyDistance = Infinity;
  let anyEnergyDistance = Infinity;
  for (const item of game.energy) {
    const itemDistance = distance(bot.x, bot.y, item.x, item.y);
    if (itemDistance < anyEnergyDistance) {
      anyEnergy = item;
      anyEnergyDistance = itemDistance;
    }
    if (item.owner === bot.id && itemDistance < ownEnergyDistance) {
      ownEnergy = item;
      ownEnergyDistance = itemDistance;
    }
  }

  // A bot that only sets off once the storm opens arrives to find it half spent.
  // Reading the warning is what a person does, and it is what makes everybody
  // converge on the same spot at the same moment instead of trickling in.
  let stormTarget: { x: number; y: number } | undefined;
  const storm = getStormState(
    Number.isFinite(game.elapsed) ? game.elapsed : 0,
    settings.stationInterval,
    settings.stationActiveDuration,
  );
  const stormRoom = settings.stationBonus - (game.stormTaken?.get(bot.id) ?? 0);
  if ((storm.active || storm.warning) && bot.orbs < settings.maxOrbs && stormRoom > 0) {
    stormTarget = getStormCenter(storm.cycle, game.width, game.height, settings.stationRadius);
  }

  // Stick to the pile it set off for. Re-choosing every tick meant two piles a
  // pixel apart in value had it walking between them and reaching neither.
  const held = commitment(game, bot, () => {
    const pick = stormTarget ? null : ownEnergy ?? anyEnergy;
    return pick ? { kind: "gather", id: String(pick.id) } : null;
  });
  const committed = held?.kind === "gather"
    ? game.energy.find((item) => String(item.id) === held.id)
    : undefined;

  const gatherTarget = stormTarget ?? committed ?? ownEnergy ?? anyEnergy;
  let moveX = gatherTarget?.x ?? bot.targetX;
  let moveY = gatherTarget?.y ?? bot.targetY;
  let useBoost = false;
  const targetDistance = target ? distance(bot.x, bot.y, target.x, target.y) : Infinity;
  // A closed ring is a shot worth two to four orbs, and it expires by being spent.
  // Carrying one and walking back to the energy field is the one thing a person
  // would never do.
  const charged = chargeOf(bot) > 0;
  const needsEnergy = bot.orbs <= 3 && !charged;

  if (target && charged && targetDistance <= settings.shotRange) {
    // Close the distance and take the shot it has already paid for.
    const dx = target.x - bot.x;
    const dy = target.y - bot.y;
    const length = Math.max(1, Math.hypot(dx, dy));
    moveX = bot.x + (dx / length) * 240;
    moveY = bot.y + (dy / length) * 240;
    useBoost = targetDistance > 300 && bot.orbs >= 5 && bot.boostRemaining <= 0;
  } else if (target && !needsEnergy) {
    const dx = target.x - bot.x;
    const dy = target.y - bot.y;
    const length = Math.max(1, Math.hypot(dx, dy));
    const strongerThreat = target.orbs > bot.orbs + 2;
    if (targetDistance < 155 || (strongerThreat && targetDistance < 245)) {
      moveX = bot.x - (dx / length) * 210;
      moveY = bot.y - (dy / length) * 210;
      useBoost = strongerThreat && bot.orbs >= 4 && bot.boostRemaining <= 0;
    } else if (targetDistance > 285) {
      moveX = target.x;
      moveY = target.y;
    } else {
      moveX = bot.x + (-dy / length) * 180 * side;
      moveY = bot.y + (dx / length) * 180 * side;
    }
    if (target.orbs === 0 && targetDistance > 285 && targetDistance < settings.shotRange && bot.orbs >= 5 && bot.boostRemaining <= 0) {
      useBoost = true;
    }
  }

  let fireX: number | null = null;
  let fireY: number | null = null;
  const canRiskShot = bot.orbs > 1 || target?.orbs === 0;
  if (target && canRiskShot && targetDistance <= settings.shotRange * 0.94) {
    const travelTime = targetDistance / Math.max(1, settings.shotSpeed);
    const leadTime = Math.min(0.8, travelTime) * skill;
    const error = (1 - skill) * 24;
    const seed = jitterSeed(bot) * 20;
    fireX = target.x + target.vx * leadTime + Math.sin(seed * 13.7 + game.timeLeft * 0.8) * error;
    fireY = target.y + target.vy * leadTime + Math.cos(seed * 9.3 + game.timeLeft * 0.8) * error;
  }

  return { moveX, moveY, fireX, fireY, useBoost, useShield: false };
}
